import React, { useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin } from 'lucide-react';
import { Unit } from '../../types';

interface Point {
  id: string;
  name: string;
  battery: number;
  lat: number;
  lng: number;
}

const parsePoints = (units: Unit[]): Point[] => {
  const points: Point[] = [];
  for (const u of units) {
    if (!u.coords) continue;
    try {
      const c = JSON.parse(u.coords);
      const lat = Number(c?.latitude);
      const lng = Number(c?.longitude);
      if (Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0)) {
        points.push({ id: u.id, name: u.name, battery: u.battery ?? 0, lat, lng });
      }
    } catch {
      /* ignore malformed coords */
    }
  }
  return points;
};

const colorFor = (battery: number) => (battery < 20 ? '#ef4444' : battery < 50 ? '#f59e0b' : '#10b981');

const pinIcon = (battery: number) =>
  L.divIcon({
    className: '',
    html: `<div style="width:18px;height:18px;border-radius:9999px;background:${colorFor(
      battery
    )};border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4)"></div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });

const FitBounds: React.FC<{ points: Point[] }> = ({ points }) => {
  const map = useMap();
  React.useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 14);
      return;
    }
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng] as [number, number]));
    map.fitBounds(bounds, { padding: [32, 32] });
  }, [points, map]);
  return null;
};

const LocksMap: React.FC<{ units: Unit[] }> = ({ units }) => {
  const points = useMemo(() => parsePoints(units), [units]);

  if (points.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center text-slate-400 gap-2">
        <MapPin className="h-8 w-8" />
        <p className="text-sm">No locks with location yet</p>
      </div>
    );
  }

  const center: [number, number] = [points[0].lat, points[0].lng];

  return (
    <MapContainer
      center={center}
      zoom={12}
      scrollWheelZoom={false}
      className="h-full w-full rounded-xl"
      style={{ background: '#e2e8f0' }}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <FitBounds points={points} />
      {points.map((p) => (
        <Marker key={p.id} position={[p.lat, p.lng]} icon={pinIcon(p.battery)}>
          <Popup>
            <div className="text-sm">
              <div className="font-semibold text-slate-900">{p.name}</div>
              <div className="text-slate-500">Battery: {p.battery}%</div>
            </div>
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
};

export default LocksMap;
